import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    PaperProps,
    Tooltip,
} from "@mui/material";
import React, { ReactNode, useCallback } from "react";
import { ConfirmationButton } from "../../Button/ConfirmationButton";
import { DangerButton } from "../../Button/DangerButton";
import { DraggablePaper } from "../DraggablePaper";
import { AppBaseDialog } from "../AppBaseDialog";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

export interface IDialogButton {
  title?: any;
  disabled?: boolean;
  tooltip?: string;
  icon?: ReactNode;
  variant?: "text" | "outlined" | "contained" | "confirmation" | "danger";
  color?: string;
  margin?: string;
  onClick: (id?: string) => void;
}

export interface DialogProps {
  id: string;
  open?: boolean;
  hide?: boolean;
  disableEnforceFocus?: boolean;
  dataId?: string;
  maxHeight?: number | string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  title: string | ReactNode;
  body: string | ReactNode;
  buttons?: IDialogButton[];
  actions?: ReactNode | ReactNode[];
  centerOnResize?: boolean;
  dialogTabs?: ReactNode;
  overflowY?: "visible" | "hidden" | "auto";
  onClose?: () => void;
}

export const Dialog = ({
    id,
    open = true,
    title,
    body,
    buttons,
    actions,
    dataId,
    maxWidth = "sm",
    maxHeight,
    centerOnResize,
    dialogTabs,
    overflowY = "auto",
    onClose = null,
    disableEnforceFocus
}: DialogProps) => {
    const getPaper = useCallback(
        (props: PaperProps) => (
            <DraggablePaper
                id={id}
                maxHeight={maxHeight}
                centerOnResize={centerOnResize}
                props={props}
            />
        ),
        [id, maxHeight]
    );

    return (
        <AppBaseDialog
            open={open}
            onClose={onClose}
            key={dataId}
            PaperComponent={getPaper}
            maxWidth={maxWidth}
            fullWidth
            disableEnforceFocus={disableEnforceFocus}
        >
            <DialogTitle style={{ cursor: "move" }} id={id}>
                {title}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            {dialogTabs ? <Box sx={{ margin: "0 1.5rem" }}>{dialogTabs}</Box> : null}
            <DialogContent sx={{ display: "flex", overflowX: overflowY === "visible" ? "visible" : "hidden", overflowY }}>
                <Box display="flex" flexDirection="column" flexGrow={2} width="100%">
                    {body}
                </Box>
            </DialogContent>
            {(buttons || actions) && (
                <DialogActions>
                    {buttons &&
                        buttons.length &&
                        buttons
                            .filter((button) => !!button.onClick)
                            .map((button, i) => (
                                <Tooltip key={i} title={button.tooltip} style={{ margin: button.margin }}>
                                    <span>
                                        {button.variant === "confirmation" && (
                                            <ConfirmationButton
                                                onClick={() => button.onClick()}
                                                startIcon={button.icon}
                                                disabled={button.disabled}
                                            >
                                                {button.title}
                                            </ConfirmationButton>
                                        )}
                                        {button.variant === "danger" && (
                                            <Box mr="1.5rem">
                                                <DangerButton
                                                    onClick={() => button.onClick()}
                                                    startIcon={button.icon}
                                                    disabled={button.disabled}
                                                >
                                                    {button.title}
                                                </DangerButton>
                                            </Box>
                                        )}
                                        {button.variant !== "danger" &&
                                        button.variant !== "confirmation" && (
                                            <AppButton
                                                key={i}
                                                variant={button.variant || "outlined"}
                                                onClick={() => button.onClick()}
                                                startIcon={button.icon}
                                                disabled={button.disabled}
                                            >
                                                {button.title}
                                            </AppButton>
                                        )}
                                    </span>
                                </Tooltip>
                            ))}
                    {actions || null}
                </DialogActions>
            )}
        </AppBaseDialog>
    );
};

export const CkEditorDialog = (props: DialogProps) => (
    <Dialog {...props} disableEnforceFocus={true} />
);